import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { StudioSheet } from './StudioSheet';
import { StudioIcon } from './StudioIcon';
import type { StudioApp } from './studio-api';
import { provisionalPreview, studioSampleYou, type StudioPreviewModel } from './studio-preview-model';
import { PLACEHOLDER_CAPTION, previewLook, type StudioPreviewLook } from './studio-preview-template';
import { cachedStudioPreview, loadStudioPreview } from './studio-preview-store';
import { platform } from '../../platform';
import { StudioPreviewMedia } from './StudioPreviewMedia';
import { StudioPreviewAura, StudioPreviewFrame } from './StudioPreviewFrame';
import './studio-preview.css';

/** Подвал карточки: длительность ролика моноширинным, как в needHTML прототипа («Reels · <span class="mono">0:12</span>») */
function metaParts(meta: string) {
  return meta.split(' · ').map((part, index) => (
    <Fragment key={index}>
      {index > 0 && ' · '}
      {/^\d+:\d{2}$/.test(part) ? <span className="mono">{part}</span> : part}
    </Fragment>
  ));
}
/** Цена подвала: число моноширинным, приставка «от» и «C» — одной строкой перевода («от {0} C»): порядок слов — по языку */
function priceParts(price: NonNullable<StudioPreviewModel['price']>) {
  const [head, tail = ''] = (price.from ? uiText('от {0} C') : '{0} C').split('{0}');
  return (
    <>
      {head}
      <span className="mono">{price.amount}</span>
      {tail}
    </>
  );
}
/** Слой «до» листа (baBefore прототипа): заготовка текста или темы, круги ролей сцены (ваше фото — у первой взрослой роли без
    мужского пола, у остальных силуэт: портретов показательных близких в документе нет), иначе ваше фото (.ba-me).
    Ваше фото — из документа (preview.before_url); его нет (§6.3: before_url: null) или адрес не прошёл проверку — своё «ваше фото»
    приложения, как BC.YOU() у шаблона: слой «до» с ползунком и кнопками под кадром есть всегда, и у листа из карточки тоже */
function beforeLayer(current: StudioPreviewModel, look: StudioPreviewLook, held: boolean, autoplay: boolean) {
  const caption = PLACEHOLDER_CAPTION[look.beforeKind];
  if (caption)
    return (
      <span className="pre-ph tx">
        <i />
        <i />
        <i />
        <small>{uiText(caption)}</small>
      </span>
    );
  const you = (current.before?.type === 'image' ? current.before.url : undefined) ?? studioSampleYou();
  if (look.beforeKind === 'people' && look.people.length) {
    const list = look.people.slice(0, 3),
      me = list.findIndex(role => role.k === 'man' && role.g !== 'm');
    return (
      <span className={`ba-ppl n${list.length}`}>
        {list.map((role, index) => (
          <span className="rc" key={role.id}>
            <span className="av">
              {index === me && you ? (
                <img src={you} alt="" draggable={false} referrerPolicy="no-referrer" />
              ) : (
                <i className="sil">
                  <StudioIcon kind={role.k === 'pet' ? 'heart' : 'user'} />
                </i>
              )}
            </span>
            <small>{role.name}</small>
          </span>
        ))}
      </span>
    );
  }
  if (current.before)
    return (
      <StudioPreviewMedia
        key={current.before.url}
        media={current.before}
        label={look.beforeLabel}
        held={held}
        autoplay={autoplay}
        controls={false}
        className="ba-me"
      />
    );
  return you ? <img className="ba-me" src={you} alt="" draggable={false} referrerPolicy="no-referrer" /> : undefined;
}
/** Верх листа доезжает плавно, когда документ шаблона дополнил лист, собранный из карточки: высота меняется не скачком
    (у шаблона лист выезжает уже полным, у сервера остаток приходит по сети). Тот же переход, что у входа листа (bot-core.css: .22s) */
function glideSheetTop(sheet: HTMLElement | null, fromHeight: number) {
  if (!sheet || !fromHeight || window.BotCore?.reduced?.() || document.documentElement.classList.contains('still')) return;
  /* лист прижат к низу: на сколько он вырос, на столько поднялся верх (высота не зависит от сдвига въезда, в отличие от положения) */
  const grown = sheet.offsetHeight - fromHeight;
  /* сложение с переходом въезда (composite: add) есть не во всех WebView (Safari до 16): там лист просто встаёт на место без доезда */
  if (
    Math.abs(grown) < 2 ||
    typeof sheet.animate !== 'function' ||
    typeof KeyframeEffect === 'undefined' ||
    !('composite' in KeyframeEffect.prototype)
  )
    return;
  sheet.animate([{ transform: `translateY(${grown}px)` }, { transform: 'translateY(0)' }], {
    duration: 220,
    easing: 'cubic-bezier(.2,.8,.2,1)',
    composite: 'add',
  });
}

export function StudioPreview({
  appId,
  open = true,
  tile,
  onTry,
  onClose,
  autoplay = false,
}: {
  appId: number;
  /** Лист один на «Студию» (#stPre шаблона): закрытие снимает open с того же узла, уход играет переход шаблона на нём самом */
  open?: boolean;
  /** Карточка, с которой открыт лист: пока документ в пути, лист собран из неё */
  tile?: Record<string, unknown>;
  onTry(app: StudioApp, roleId?: string): void;
  onClose(): void;
  autoplay?: boolean;
}) {
  const [loaded, setLoaded] = useState<StudioPreviewModel | null>(() => cachedStudioPreview(appId));
  const [failedKey, setFailedKey] = useState('');
  const [attempt, setAttempt] = useState(0),
    [held, setHeld] = useState(false);
  /* каждое открытие — заново, как preHTML шаблона: раздел «до/после» посередине, подсказка снова играет */
  const [opens, setOpens] = useState(0),
    [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpens(opens + 1);
  }
  useEffect(() => {
    /* загруженный документ лист читает прямо при отрисовке (full ниже) */
    if (cachedStudioPreview(appId)) return;
    let live = true;
    loadStudioPreview(appId).then(
      value => {
        if (live) setLoaded(value);
      },
      () => {
        if (live) setFailedKey(`${appId}:${attempt}`);
      },
    );
    return () => {
      live = false;
    };
  }, [appId, attempt]);
  // Старое приложение не становится действием нового листа во время загрузки.
  /* загруженный заранее документ читается в том же кадре: лист другого шаблона не мелькает собранным из карточки */
  const full = loaded?.app.id === appId ? loaded : cachedStudioPreview(appId);
  const currentError = !full && failedKey === `${appId}:${attempt}` ? uiText('Не удалось загрузить шаблон. Обновите превью.') : '';
  const draft = full || !tile || tile.id !== appId ? null : provisionalPreview(tile);
  const current = full ?? draft;
  const look = current ? previewLook(current) : null;
  /* лист из карточки дополнился документом: верх листа доезжает, а не прыгает */
  const draftHeight = useRef(0);
  useLayoutEffect(() => {
    const sheet = document.getElementById('stPre');
    if (full && draftHeight.current) glideSheetTop(sheet, draftHeight.current);
    draftHeight.current = !full && draft && sheet ? sheet.offsetHeight : 0;
  }, [full, draft?.app.id]); // eslint-disable-line react-hooks/exhaustive-deps
  function choose(roleId?: string) {
    if (!full) return;
    /* строка роли — с отдачей, как у шаблона (data-pre-role: ui.buzz — impactOccurred('medium')); кнопка листа — без */
    if (roleId !== undefined) platform.haptic('medium');
    onClose();
    onTry(full.app, roleId);
  }
  return (
    <StudioSheet
      title="Шаблон"
      id="stPre"
      open={open}
      legacyShell
      closeLabel="Закрыть превью"
      onClose={onClose}
      /* аура — первым ребёнком #stPre, как в preHTML шаблона: под телом листа, не под его маской */
      backdrop={<StudioPreviewAura cover={current?.after?.type === 'image' ? current.after.url : current?.after?.poster} />}
      footer={
        look ? (
          <button type="button" className="btn" disabled={!full} aria-busy={!full} onClick={() => choose()}>
            {uiText(look.cast ? 'Выбрать, кто играет' : 'Попробовать')}
          </button>
        ) : undefined
      }
    >
      <section className={`studio-preview st-prein${current?.format ? ` f-${current.format}` : ''}`}>
        {!current && !currentError && <p role="status">{uiText('Загружаем шаблон…')}</p>}
        {currentError && (
          <p role="alert">
            {currentError}
            <button
              type="button"
              onClick={() => {
                setFailedKey('');
                setAttempt(value => value + 1);
              }}
            >
              {uiText('Повторить загрузку')}
            </button>
          </p>
        )}
        {current && look && (
          <>
            <StudioPreviewFrame
              key={`${appId}:${opens}`}
              label={uiFormat('Сравнить: {0} и {1}', look.beforeLabel, look.afterLabel.toLowerCase())}
              beforeLabel={look.beforeLabel}
              afterLabel={look.afterLabel}
              after={
                current.after ? (
                  <StudioPreviewMedia
                    key={current.after.url}
                    media={current.after}
                    label={look.afterLabel}
                    held={held}
                    autoplay={autoplay && open}
                  />
                ) : (
                  <span className="pre-ph">
                    <small>{uiText('Пример результата пока не опубликован')}</small>
                  </span>
                )
              }
              before={beforeLayer(current, look, held, autoplay && open)}
            />
            {current.after?.type === 'video' && autoplay && open && (
              <button
                type="button"
                onPointerDown={() => setHeld(true)}
                onPointerUp={() => setHeld(false)}
                onPointerCancel={() => setHeld(false)}
                onPointerLeave={() => setHeld(false)}
                onBlur={() => setHeld(false)}
                onKeyDown={event => {
                  if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    setHeld(true);
                  }
                }}
                onKeyUp={() => setHeld(false)}
              >
                {uiText('Удерживайте для просмотра')}
              </button>
            )}
            <h2 className="pre-t">{current.title}</h2>
            {look.plot && <p className="pre-d">{look.plot}</p>}
            {/* карточка «Что нужно» (needHTML прототипа): шапка, роли сцены строками или строки по формату, подвал с ценой */}
            <section className="pre-nd" aria-label={uiText(current.roles.length ? 'Кто играет' : 'Что нужно')}>
              {/* строк нет (формат без своих строк) — шапки тоже нет, остаётся подвал с ценой */}
              {(current.provisional || current.roles.length > 0 || look.rows.length > 0) && (
                <p className="pre-ndh">{uiText(current.roles.length ? 'Кто играет' : 'Что нужно')}</p>
              )}
              {/* лист из карточки: место под строки «Что нужно» занято заранее, документ встаёт в него, лист почти не растёт */}
              {current.provisional && <div className="pre-nd-wait" role="status" aria-label={uiText('Загружаем шаблон…')} />}
              {current.roles.map(role => (
                <button
                  className="pre-nr rl"
                  type="button"
                  key={role.id}
                  aria-label={uiFormat('Выбрать, кто играет: {0}', role.name)}
                  onClick={() => choose(role.id)}
                >
                  <span className="ic pl">
                    <StudioIcon kind="plus" />
                  </span>
                  <span className="m">
                    <b>{role.name}</b>
                    <span>{look.subtitle(role)}</span>
                  </span>
                  <span className="rt">
                    <StudioIcon kind="chev" />
                  </span>
                </button>
              ))}
              {look.rows.map((need, index) => (
                <div className="pre-nr" key={index}>
                  <span className="ic">
                    <StudioIcon kind={need.icon} />
                  </span>
                  <span className="m">
                    <b>{need.title}</b>
                    {need.hint && <span>{need.hint}</span>}
                  </span>
                </div>
              ))}
              <div className="pre-ndf">
                <span className="mt">{look.meta && metaParts(look.meta)}</span>
                {current.price ? (
                  // Подвал карточки: число моноширинным и единица «C», как в needHTML прототипа
                  <b className="pr">
                    {priceParts(current.price)}
                    {current.price.unit === 'per_second' && <span className="u">{uiText('за секунду')}</span>}
                    {current.price.unit === 'per_minute' && <span className="u">{uiText('в минуту')}</span>}
                    {current.price.unit === 'per_100_chars' && <span className="u">{uiText('за 100 знаков')}</span>}
                  </b>
                ) : (
                  <span className="pr">{uiText('Цена уточняется в форме создания.')}</span>
                )}
              </div>
            </section>
          </>
        )}
      </section>
    </StudioSheet>
  );
}
